import { Alert, Button, Card, Descriptions, Form, Input, Space, Tag, Typography } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../api/client';
import DriverLiveMap from '../../components/DriverLiveMap';
import { antdHongKongPhoneRules, formatHongKongPhoneDisplay } from '../../firebase/phone';
import { labelDriverTier } from '../../i18n/crmLabels';

type LiveSnapshot = {
  lat: number;
  lng: number;
  heading: number | null;
  speedKmh: number | null;
  updatedAt: string;
};

type LookupResult = {
  driver: {
    id: string;
    phone: string;
    offDutyPhone: string | null;
    displayName: string | null;
    vehiclePlate: string | null;
    isOnline: boolean;
    driverTier: string;
    matchedPhoneField: 'work' | 'off_duty';
  };
  live: LiveSnapshot | null;
};

const STALE_MINUTES = 15;

function formatHkDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('zh-HK', { timeZone: 'Asia/Hong_Kong', hour12: false });
  } catch {
    return iso;
  }
}

function ageMinutes(iso: string): number {
  return (Date.now() - new Date(iso).getTime()) / 60_000;
}

export default function DriverLiveLocationPage() {
  const [form] = Form.useForm<{ phone: string }>();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<LookupResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const phoneRef = useRef('');

  const fetchLive = useCallback(async (phone: string, silent = false) => {
    const trimmed = phone.trim();
    if (!trimmed) return;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const { data } = await api.get<LookupResult>('/admin/drivers/live-location/by-phone', {
        params: { phone: trimmed },
      });
      setResult(data);
    } catch (e: unknown) {
      setResult(null);
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '查詢失敗';
      setError(String(msg));
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!result?.live) return;
    const id = setInterval(() => {
      void fetchLive(phoneRef.current, true);
    }, 12_000);
    return () => clearInterval(id);
  }, [result?.driver.id, result?.live, fetchLive]);

  const onSearch = async (values: { phone: string }) => {
    phoneRef.current = values.phone.trim();
    await fetchLive(values.phone);
  };

  const live = result?.live;
  const stale = live ? ageMinutes(live.updatedAt) > STALE_MINUTES : false;

  return (
    <Card>
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        司機即時位置
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        輸入司機開工或收工電話，顯示司機 App 最近一次上報的 GPS（資料來自 live_locations）。
      </Typography.Paragraph>

      <Form form={form} layout="inline" onFinish={(v) => void onSearch(v)} style={{ marginBottom: 16 }}>
        <Form.Item name="phone" rules={antdHongKongPhoneRules()} style={{ minWidth: 220 }}>
          <Input placeholder="91234567" inputMode="numeric" maxLength={16} />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading}>
            查詢
          </Button>
        </Form.Item>
      </Form>

      {error ? <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} /> : null}

      {result ? (
        <>
          <Descriptions
            bordered
            size="small"
            column={{ xs: 1, sm: 1, md: 2, lg: 2 }}
            style={{ marginBottom: 16 }}
          >
            <Descriptions.Item label="司機 ID">{result.driver.id}</Descriptions.Item>
            <Descriptions.Item label="姓名">{result.driver.displayName ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="開工電話">
              {formatHongKongPhoneDisplay(result.driver.phone)}
            </Descriptions.Item>
            <Descriptions.Item label="收工電話">
              {result.driver.offDutyPhone
                ? formatHongKongPhoneDisplay(result.driver.offDutyPhone)
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="車牌">{result.driver.vehiclePlate ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="級別">{labelDriverTier(result.driver.driverTier)}</Descriptions.Item>
            <Descriptions.Item label="上線狀態">
              {result.driver.isOnline ? <Tag color="green">上線</Tag> : <Tag>離線</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="匹配電話">
              {result.driver.matchedPhoneField === 'work' ? '開工電話' : '收工電話'}
            </Descriptions.Item>
          </Descriptions>

          {!live ? (
            <Alert
              type="warning"
              showIcon
              message="尚無 GPS 紀錄"
              description="司機可能未上線、已收工，或 App 尚未上報位置。"
            />
          ) : (
            <>
              <Space wrap style={{ marginBottom: 12 }}>
                <Typography.Text>
                  最後 GPS：<Typography.Text strong>{formatHkDateTime(live.updatedAt)}</Typography.Text>
                  （香港時間）
                </Typography.Text>
                {stale ? (
                  <Tag color="orange">超過 {STALE_MINUTES} 分鐘未更新</Tag>
                ) : (
                  <Tag color="blue">約 {Math.max(0, Math.round(ageMinutes(live.updatedAt)))} 分鐘前</Tag>
                )}
                <Typography.Text type="secondary">
                  {live.lat.toFixed(6)}, {live.lng.toFixed(6)}
                </Typography.Text>
                {live.heading != null ? (
                  <Typography.Text type="secondary">方向 {live.heading.toFixed(0)}°</Typography.Text>
                ) : null}
                {live.speedKmh != null ? (
                  <Typography.Text type="secondary">速度 {live.speedKmh.toFixed(0)} km/h</Typography.Text>
                ) : null}
              </Space>
              <DriverLiveMap
                lat={live.lat}
                lng={live.lng}
                label={result.driver.displayName ?? result.driver.vehiclePlate ?? '司機'}
              />
              <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                地圖每 12 秒自動刷新位置；底圖為 OpenStreetMap（唔消耗 Google Maps API）。
              </Typography.Paragraph>
            </>
          )}
        </>
      ) : null}
    </Card>
  );
}
